'use client';

import axios from 'axios';

export interface PaystackPaymentData {
  email: string;
  amount: number; // Amount in kobo (multiply GMD by 100)
  reference: string;
  metadata?: {
    orderId?: string;
    customerId?: string;
    customerName?: string;
    phone?: string;
    [key: string]: any;
  };
}

export interface PaystackVerificationResponse {
  status: boolean;
  message: string;
  data: {
    id: number;
    domain: string;
    status: string;
    reference: string;
    amount: number;
    message: string | null;
    gateway_response: string;
    paid_at: string;
    created_at: string;
    channel: string;
    currency: string;
    ip_address: string;
    metadata: any;
    fees: number;
    customer: {
      id: number;
      first_name: string | null;
      last_name: string | null;
      email: string;
      customer_code: string;
      phone: string | null;
      metadata: any;
    };
    authorization: {
      authorization_code: string;
      bin: string;
      last4: string;
      exp_month: string;
      exp_year: string;
      channel: string;
      card_type: string;
      bank: string;
      country_code: string;
      brand: string;
      reusable: boolean;
      signature: string;
      account_name: string | null;
    };
  };
}

class PaystackService {
  private publicKey: string;
  private secretKey: string;
  private baseUrl = 'https://api.paystack.co';
  private paystackPopup: any = null;

  constructor() {
    this.publicKey = process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY || '';
    this.secretKey = process.env.PAYSTACK_SECRET_KEY || '';
  }

  /**
   * Load Paystack popup dynamically
   */
  private async loadPaystackPopup(): Promise<any> {
    if (typeof window === 'undefined') {
      throw new Error('Paystack can only be initialized on the client side');
    }

    if (this.paystackPopup) {
      return this.paystackPopup;
    }

    // Dynamically import the @paystack/inline-js package
    const PaystackPop = (await import('@paystack/inline-js')).default;
    this.paystackPopup = new PaystackPop();
    return this.paystackPopup;
  }

  /**
   * Initialize Paystack payment popup
   * This should be called from the client-side component
   */
  async initializePayment(
    paymentData: PaystackPaymentData,
    onSuccess: (reference: string) => void,
    onClose: () => void
  ): Promise<void> {
    try {
      const popup = await this.loadPaystackPopup();
      
      popup.newTransaction({
        key: this.publicKey,
        email: paymentData.email,
        amount: paymentData.amount, // Amount in kobo
        ref: paymentData.reference,
        metadata: paymentData.metadata,
        currency: 'GMD', // Gambian Dalasi
        channels: ['card', 'bank', 'mobile_money'], // Support all payment methods
        onSuccess: (transaction: any) => {
          onSuccess(transaction.reference);
        },
        onCancel: () => {
          onClose();
        },
      });
    } catch (error: any) {
      console.error('Paystack initialization error:', error);
      throw new Error(error?.message || 'Failed to initialize payment');
    }
  }

  /**
   * Generate a unique payment reference
   */
  generateReference(): string {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000000);
    return `TLL-${timestamp}-${random}`;
  }

  /**
   * Verify payment transaction (server-side only)
   * This should be called from an API route
   */
  async verifyPayment(reference: string): Promise<PaystackVerificationResponse> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/transaction/verify/${reference}`,
        {
          headers: {
            Authorization: `Bearer ${this.secretKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message || 'Failed to verify payment'
      );
    }
  }

  /**
   * Convert GMD amount to kobo (smallest currency unit)
   * Paystack requires amount in kobo (1 GMD = 100 kobo)
   */
  convertToKobo(amount: number): number {
    return Math.round(amount * 100);
  }

  /**
   * Convert kobo to GMD
   */
  convertToGMD(kobo: number): number {
    return kobo / 100;
  }
}

export const paystackService = new PaystackService();
